
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Clock } from 'lucide-react';
import Button from '../base/Button';
import { fetchPublishedArticles, Article } from '../../utils/articleApi';
import { articleHasCategory, getArticleCategoryList } from '../../utils/articleCategories';
import ArticleCategoryBadges from './ArticleCategoryBadges';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ALL = 'all';

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(ALL);
  const [publishedArticles, setPublishedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<Article[]>([]);

  const categoryOptions = useMemo(() => {
    const names = Array.from(new Set(publishedArticles.flatMap((a) => getArticleCategoryList(a)))).sort();
    return [{ id: ALL, name: 'すべて' }, ...names.map((n) => ({ id: n, name: n }))];
  }, [publishedArticles]);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = 'unset';
      return;
    }
    document.body.style.overflow = 'hidden';
    setSearchQuery('');
    setSelectedCategory(ALL);
    setPublishedArticles([]);
    setSearchResults([]);
    let cancelled = false;
    setLoading(true);
    (async () => {
      const raw = await fetchPublishedArticles();
      const published = raw.filter((a) => a.status === '公開');
      if (!cancelled) {
        setPublishedArticles(published);
        setSearchResults(published);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || publishedArticles.length === 0) {
      return;
    }
    const q = searchQuery.trim().toLowerCase();
    const filtered = publishedArticles.filter((article) => {
      const tagStr = (article.tags || []).join(' ').toLowerCase();
      const matchesQuery =
        q === '' ||
        article.title.toLowerCase().includes(q) ||
        article.excerpt.toLowerCase().includes(q) ||
        tagStr.includes(q);
      const matchesCategory =
        selectedCategory === ALL || articleHasCategory(article, selectedCategory);
      return matchesQuery && matchesCategory;
    });
    setSearchResults(filtered);
  }, [searchQuery, selectedCategory, publishedArticles, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      <div className="relative min-h-screen flex items-start justify-center p-4 pt-16">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">旅行記事を検索</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-6 border-b border-gray-200">
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="旅行記事のタイトルやキーワードで検索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#02D0F0] focus:border-transparent text-sm"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {categoryOptions.map((category) => (
                  <button
                    type="button"
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r from-[#02D0F0] to-[#07DBCA] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-96">
            {loading ? (
              <div className="text-center py-12 text-gray-500 text-sm">読み込み中...</div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  {searchResults.length}件の旅行記事が見つかりました
                </p>
                {searchResults.map((article) => (
                  <div
                    key={article.id}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate(`/articles/${article.id}`);
                        onClose();
                      }
                    }}
                    className="flex gap-4 p-4 border border-gray-200 rounded-xl hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() => {
                      navigate(`/articles/${article.id}`);
                      onClose();
                    }}
                  >
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-24 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 group-hover:text-[#02D0F0] transition-colors line-clamp-1">
                        {article.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <ArticleCategoryBadges
                          article={article}
                          badgeClassName="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                        />
                        <span className="text-xs text-gray-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {article.readTime}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mb-4 mx-auto" />
                <p className="text-gray-500">検索条件に一致する旅行記事が見つかりませんでした</p>
                <p className="text-sm text-gray-400 mt-2">
                  別のキーワードやカテゴリで検索してみてください
                </p>
              </div>
            )}
          </div>

          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                気になる旅行記事をクリックして詳細をご覧ください
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                className="whitespace-nowrap cursor-pointer"
              >
                閉じる
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
